import { OpenAPIHousien } from "@services/requests/core/OpenAPI";
import { request as __request } from "@services/requests/core/request";
import { ApiError } from "@services/requests/core/ApiError";

/**
 * One server-computed count bucket in a property aggregation. Mirrors the
 * backend `GET /agencies/{agencyId}/properties/aggregate` bucket shape — the
 * SAME endpoint the agent's `aggregate_properties` / `build_dashboard` tools
 * consume, so a dashboard bucket count rendered here EQUALS the agent's count.
 */
export interface AggregationBucket {
  /** The bucket key (e.g. "2", "5+", "Campinas"). */
  key: string;
  /** The server-computed count of properties in the bucket. */
  count: number;
}

/**
 * The result of one dimension aggregation. `supported` is `false` when the
 * backend rejected the dimension (HTTP 400); `allowed` then lists the
 * dimensions it accepts, so the UI can render the honest "não é possível
 * agrupar por X" message instead of a silent empty panel.
 */
export interface AggregationResult {
  /** The dimension aggregated by. */
  groupBy: string;
  /** Sorted server-computed buckets. */
  buckets: AggregationBucket[];
  /** Total distinct properties aggregated. */
  total: number;
  /** `false` when the backend rejected the dimension. */
  supported: boolean;
  /** When unsupported, the dimensions the backend accepts. */
  allowed?: string[];
}

interface AggregateResponseBody {
  buckets?: AggregationBucket[];
  total?: number;
  group_by?: string;
}

/**
 * The group-by dimensions the backend aggregation route supports. Kept
 * identical to the agent's `aggregate_properties` / `build_dashboard` enum so
 * a dashboard dimension here can never diverge from what the agent composes.
 */
export const DASHBOARD_DIMENSIONS = [
  "bedrooms",
  "bathrooms",
  "suites",
  "parking_spots",
  "total_rooms",
  "category",
  "city",
  "state",
  "neighborhood",
  "transaction_type",
  "total_area",
  "constructed_area",
  "sale_price",
  "rental_price",
] as const;

export type DashboardDimension = (typeof DASHBOARD_DIMENSIONS)[number];

function parseAllowed(error: ApiError): string[] | undefined {
  const body = error.body as { detail?: string; allowed?: string[] } | undefined;
  if (body?.allowed && Array.isArray(body.allowed)) {
    return body.allowed;
  }
  // The backend 400 message carries "Allowed values: ['a', 'b', ...]".
  const detail = body?.detail ?? "";
  const match = /Allowed values:\s*\[([^\]]*)\]/.exec(detail);
  if (!match) {
    return undefined;
  }
  return match[1]
    .split(",")
    .map((item) => item.replace(/['"\s]/g, ""))
    .filter(Boolean);
}

/**
 * Fetches one dimension aggregation from the backend, mapping an HTTP 400
 * (unsupported dimension) into an honest `supported: false` result rather than
 * throwing — so the dashboard surfaces the "não é possível agrupar por X"
 * message, never a fabricated empty panel.
 *
 * @param agencyId - The agency whose portfolio is aggregated.
 * @param groupBy - The dimension to group by (one of DASHBOARD_DIMENSIONS).
 * @returns The server-computed aggregation result.
 */
export async function fetchAggregation(
  agencyId: string,
  groupBy: string,
): Promise<AggregationResult> {
  try {
    const body = await __request<AggregateResponseBody>(OpenAPIHousien, {
      method: "GET",
      url: "/agencies/{agencyId}/properties/aggregate",
      path: { agencyId },
      query: { group_by: groupBy },
    });
    return {
      groupBy,
      buckets: body.buckets ?? [],
      total: body.total ?? 0,
      supported: true,
    };
  } catch (error) {
    if (error instanceof ApiError && error.status === 400) {
      return {
        groupBy,
        buckets: [],
        total: 0,
        supported: false,
        allowed: parseAllowed(error),
      };
    }
    throw error;
  }
}

/**
 * A saved dashboard SPEC — a title plus the ordered dimensions whose panels it
 * renders. Numbers are NEVER stored; opening a dashboard re-runs
 * {@link fetchAggregation} live so counts are always current.
 */
export interface DashboardSpec {
  /** Stable id for the saved dashboard. */
  id: string;
  /** Human title. */
  title: string;
  /** The ordered dimensions whose panels the dashboard renders. */
  dimensions: DashboardDimension[];
  /** ISO timestamp of the last update, for the list subtitle. */
  updatedAt: string;
}

/**
 * Fetches every panel for a dashboard spec live, in parallel, so the rendered
 * counts are always freshly recomputed (never stale cached numbers).
 *
 * @param agencyId - The agency to aggregate.
 * @param dimensions - The spec's ordered dimensions.
 * @returns One aggregation result per dimension, in spec order.
 */
export function fetchDashboardPanels(
  agencyId: string,
  dimensions: readonly string[],
): Promise<AggregationResult[]> {
  return Promise.all(dimensions.map((dim) => fetchAggregation(agencyId, dim)));
}
